import { inject, Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import {
  getPossibleLanguages,
  ILanguage,
  SupportedLanguages,
} from "../models/language.model";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  private translocoService = inject(TranslocoService);
  private language: SupportedLanguages = SupportedLanguages.fr;

  public getCurrentLanguage(): ILanguage {
    const storedLang = window.localStorage.getItem("language");
    if (storedLang) {
      this.language = this.getSupportedLanguage(storedLang);
    } else {
      const browserLang = this.getBrowserLanguage();
      this.language = this.getSupportedLanguage(browserLang);
    }
    this.setLanguage(this.language);
    return getPossibleLanguages().find(
      (lang) => lang.short === this.language,
    ) as ILanguage;
  }

  public getSupportedLanguage(language: string): SupportedLanguages {
    return ["fr", "nl", "en"].includes(language)
      ? (language as SupportedLanguages)
      : SupportedLanguages.fr;
  }

  private getBrowserLanguage(): string {
    const fullLang = navigator.language;
    const primaryLang = fullLang.split("-")[0];
    const availableLangs =
      this.translocoService.getAvailableLangs() as string[];

    if (availableLangs.includes(primaryLang)) {
      return primaryLang;
    }
    return this.translocoService.getDefaultLang();
  }

  public setLanguage(language: SupportedLanguages): void {
    this.language = language;
    window.localStorage.setItem("language", language);
    void this.setTranslateServiceLang(language);
  }

  private async setTranslateServiceLang(
    language: SupportedLanguages,
  ): Promise<void> {
    if (this.translocoService.getActiveLang() === language) return;

    try {
      const json = await import(`../../assets/i18n/${language}.json`);
      this.translocoService.setTranslation(json.default, language, {});
      this.translocoService.setActiveLang(language);
    } catch (error) {
      console.error(`Failed to load language file for ${language}:`, error);
      // Fallback to default language if loading fails
      if (language !== SupportedLanguages.nl) {
        void this.setTranslateServiceLang(SupportedLanguages.nl);
      }
    }
  }

  public async initializeTranslateService() {
    await this.setTranslateServiceLang(
      this.getCurrentLanguage().short as SupportedLanguages,
    );
  }
}
